import React, { useContext, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { FilterMatchMode, SortOrder } from 'primereact/api';
import { postEntities } from 'app/api/generalApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ToastContext } from 'app/context/ToastContext';
import ImportDataWidget from 'app/shared/protocol/import-data-widget';
import { Dropdown } from 'primereact/dropdown';
import { getAllLightCompanies } from 'app/api/insuranceCompanyApi';
import { deleteImportedProvision, regenerateNotProcessedImportedProvisions, regenerateSingleProvision } from 'app/api/provisionApi';
import axios from 'axios';
import { formatDateFromInstant, formatFromLocalDate } from 'app/shared/util/date-utils';
import { decode, encode } from 'app/shared/util/encode-utils';
import { downloadExcelFile } from 'app/api/apiUtil';
import { Menu } from 'primereact/menu';
import { InputText } from 'primereact/inputtext';

const ProvisionTable: React.FC = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const initData = searchParams.get('p') ? (decode(searchParams.get('p'), {}) as any) : null;

  const { showSuccess, showError } = useContext(ToastContext);

  const [loading, setLoading] = useState(true);
  const [provisions, setProvisions] = useState([]);
  const [entity, setEntity] = useState(null);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [selectedInsuranceCompany, setSelectedInsuranceCompany] = useState(null);
  const [deleteContactDialog, setDeleteContactDialog] = useState(false);
  const toast = useRef(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandedRows, setExpandedRows] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [filterValue, setFilterValue] = useState<string | null>(null);
  const [lazyParams, setLazyParams] = useState({
    first: initData?.page ? initData?.page * 20 : 0,
    rows: 20,
    page: initData?.page ?? 0,
    sortField: initData?.sort ?? 'id',
    sortOrder: initData?.dirertion ? (initData?.direction === 2 ? SortOrder.DESC : SortOrder.ASC) : SortOrder.DESC,
    filter: '',
    filters: null,
  });
  const [showDialog, setShowDialog] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [statusMessages, setStatusMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [filterValues, setFilterValues] = useState(
    initData?.filters || {
      processed: { value: null, matchMode: FilterMatchMode.EQUALS },
      id: { value: null, matchMode: FilterMatchMode.EQUALS },
      createdAt: { value: null, matchMode: FilterMatchMode.CUSTOM },
      type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      risk: { value: null, matchMode: FilterMatchMode.CONTAINS },
      proposalNr: { value: null, matchMode: FilterMatchMode.CONTAINS },
      clientName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      importedInsuranceCompany: { value: null, matchMode: FilterMatchMode.CONTAINS },
      insuranceCompany: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  );
  const [additionalHeaderFilters, setAdditionalHeaderFilters] = useState<any>(() => {
    let saved: any = localStorage.getItem('provisionFromToFilterRaw');
    const additional = localStorage.getItem('provisionFromToFilterAdditional');

    try {
      saved = saved ? JSON.parse(saved) : null;
    } catch (err) {
      console.warn(err);
      saved = null;
    }

    return saved?.to && saved?.from && additional ? additional : null;
  });
  const [rawAdditionalHeaderFilters, setRawAdditionalHeaderFilters] = useState<any>(() => {
    const saved = localStorage.getItem('provisionFromToFilterRaw');
    const additional = localStorage.getItem('provisionFromToFilterAdditional');
    try {
      return saved && additional ? JSON.parse(saved) : null;
    } catch (err) {
      console.warn(err);
    }
    return null;
  });

  useEffect(() => {
    getAllLightCompanies()
      .then(response => {
        setInsuranceCompanies(response.data?.map(i => ({ value: i.id, label: i.name })));
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', 'Nastala chyba pri načítaní spoločností');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [lazyParams, additionalHeaderFilters]);

  const loadData = () => {
    setLoading(true);

    const sort = lazyParams.sortField
      ? lazyParams.sortField + (lazyParams.sortOrder ? ',' + (lazyParams.sortOrder === SortOrder.DESC ? 'desc' : 'asc') : '')
      : null;

    const requestData = {
      page: lazyParams.page,
      size: lazyParams.rows,
      sort,
      ...(lazyParams.filter || additionalHeaderFilters
        ? {
            query: lazyParams.filter ?? null,
            additionalFilters: additionalHeaderFilters,
          }
        : {}),
    };

    postEntities('/api/provisions/search', requestData, filterValues)
      .then(response => {
        setProvisions(response.data.content);
        setTotalRecords(response.data.totalElements);
      })
      .catch(() => {
        showError('Chyba', 'Nastala chyba pri načítaní provízií.');
      })
      .finally(() => {
        setLoading(false);
        setIsInitialized(true);
      });
  };

  const onPage = event => {
    setLazyParams({ ...event, filter: filterValue });
  };

  const onSort = event => {
    setLazyParams({ ...event, page: lazyParams.page, filter: filterValue });
  };

  const handleCreate = () => {
    navigate(`/provisions/create-provision?p=${getTempParameters()}`); // Navigate to the creation page
  };

  useEffect(() => {
    if (filterValue || filterValues === '' || filterValues) {
      const delayDebounceFn = setTimeout(() => {
        if (isInitialized) {
          setLazyParams({
            ...lazyParams,
            first: 0,
            page: 0,
            filter: filterValue,
          });
        } else {
          setLazyParams({
            ...lazyParams,
            filter: filterValue,
          });
        }
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [filterValue, filterValues]);

  const onDownloadProtocols = taskId => {
    setIsProcessing(true);

    axios
      .get(`/api/provisions/regenerate-not-processed/download/${taskId}`, {
        responseType: 'blob',
      })
      .then(response => {
        const blob = new Blob([response.data], { type: 'text/plain' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = 'protocols.txt';
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', 'Neočakávaná chyba pri sťahovaní súboru');
      })
      .finally(() => setIsProcessing(false));
  };

  const onLastElementCame = taskId => {
    setIsProcessing(false);
    showSuccess('Úspech', 'Spracovanie nespracovaných importovaných provízií bolo úspešne ukončené.');
    loadData();
  };

  const loadOtherLogs = taskId => {
    axios
      .get(`/api/provisions/regenerate-not-processed/${taskId}`)
      .then(response => {
        if (response.data) {
          setStatusMessages(prev => [...prev, ...response.data]);

          if (response.data.length > 0) {
            const lastElement = response.data[response.data.length - 1];

            if (lastElement?.last) {
              onLastElementCame(taskId);
            } else {
              setTimeout(() => {
                loadOtherLogs(taskId);
              }, 2000);
            }
          } else {
            const lastElement = statusMessages[statusMessages.length - 1];

            if (lastElement?.last) {
              onLastElementCame(taskId);
            }

            setTimeout(() => {
              loadOtherLogs(taskId);
            }, 5000);
          }
        }
      })
      .catch(e => {
        console.error(e);
        setIsProcessing(false);
      });
  };

  const loadOtherExportProvisionLogs = taskId => {
    axios
      .get(`/api/provisions/export/status/${taskId}`)
      .then(response => {
        if (!response.data) {
          setTimeout(() => {
            loadOtherExportProvisionLogs(taskId);
          }, 2000);
        } else {
          axios
            .get(`/api/provisions/export/download/${taskId}`, {
              responseType: 'blob', // Important for downloading files
            })
            .then(r => {
              downloadExcelFile(r);
            })
            .catch(e => {
              console.log(e);
              showError('Chyba', 'Neočakávaná chyba pri exporte. Skúste ešte raz.');
            })
            .finally(() => setLoading(false));
        }
      })
      .catch(e => {
        console.error(e);
        setIsProcessing(false);
      });
  };

  const handleRegenerate = () => {
    setLoading(true);
    regenerateNotProcessedImportedProvisions()
      .then(r => {
        setCurrentTaskId(r.data.taskId);
        setShowDialog(true);

        setTimeout(() => {
          loadOtherLogs(r.data.taskId);
        }, 1000);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const handleExportProvisions = () => {
    setLoading(true);

    const sort = lazyParams.sortField
      ? lazyParams.sortField + (lazyParams.sortOrder ? ',' + (lazyParams.sortOrder === SortOrder.DESC ? 'desc' : 'asc') : '')
      : null;

    const requestData = {
      page: lazyParams.page,
      size: lazyParams.rows,
      sort,
      ...(lazyParams.filter || additionalHeaderFilters
        ? {
            query: lazyParams.filter ?? null,
            additionalFilters: additionalHeaderFilters,
          }
        : {}),
    };

    postEntities('/api/provisions/export', requestData, filterValues)
      .then(response => {
        setCurrentTaskId(response.data);

        setTimeout(() => {
          loadOtherExportProvisionLogs(response.data);
        }, 1000);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  };

  function getTempParameters() {
    const p = {
      page: lazyParams.page,
      sort: lazyParams.sortField,
      direction: lazyParams.sortOrder === SortOrder.ASC ? 1 : 2,
      filters: filterValues,
      filter: filterValue,
    };
    return encode(p);
  }

  // Handle the editing of an existing agent
  const handleEdit = rowData => {
    navigate(`/provisions/create-provision/${rowData.id}?p=${getTempParameters()}`); // Navigate to the edit page
  };

  const handleDelete = () => {
    setLoading(true);
    deleteImportedProvision(entity.id)
      .then(r => {
        setProvisions(prev => prev.filter(p => r.data !== p.id));
        setDeleteContactDialog(false);
        showSuccess('Úspech', 'Provízia bola úspešne vymazaná.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const confirmDeleteContact = contactToDelete => {
    // setProduct(product);
    setEntity(contactToDelete);
    setDeleteContactDialog(true);
  };

  const handleRegenerateSingle = provisionEntity => {
    setLoading(true);

    regenerateSingleProvision(provisionEntity.id)
      .then(r => {
        showSuccess('Úspech', 'Provízia bola úspešne pregenerovaná.');
        loadData();
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const hideDeleteContactDialog = () => {
    setDeleteContactDialog(false);
  };

  const onSearchFromTo = () => {
    let additional = null;
    let raw = null;
    let save = false;

    if (
      (rawAdditionalHeaderFilters?.from && !rawAdditionalHeaderFilters?.to) ||
      (!rawAdditionalHeaderFilters?.from && rawAdditionalHeaderFilters?.to)
    ) {
      showError('Upozornenie', 'Musia byť vybraté obe hodnoty OD a DO na filtrovanie.');
      return;
    } else if (!rawAdditionalHeaderFilters?.from && !rawAdditionalHeaderFilters?.to) {
      setAdditionalHeaderFilters(null);
      save = true;
    } else {
      additional = `${rawAdditionalHeaderFilters.from},${rawAdditionalHeaderFilters.to}`;
      raw = rawAdditionalHeaderFilters;
      setAdditionalHeaderFilters(additional);
      save = true;
    }

    if (save) {
      localStorage.setItem('provisionFromToFilterRaw', JSON.stringify(raw));
      localStorage.setItem('provisionFromToFilterAdditional', additional);
    }
  };

  const actionBodyTemplate = rowData => {
    return (
      <React.Fragment>
        <div className="flex justify-content-around">
          {<Button icon="pi pi-pencil" rounded outlined onClick={() => handleEdit(rowData)} />}
          {<Button icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDeleteContact(rowData)} />}
          {<Button icon="pi pi-refresh" rounded outlined severity="warning" onClick={() => handleRegenerateSingle(rowData)} />}
        </div>
      </React.Fragment>
    );
  };

  const deleteProductDialogFooter = (
    <React.Fragment>
      <Button style={{ backgroundColor: 'gray' }} label="Nie" icon="pi pi-times" outlined onClick={hideDeleteContactDialog} />
      <Button style={{ backgroundColor: '#e31818' }} label="Áno" icon="pi pi-check" severity="danger" onClick={handleDelete} />
    </React.Fragment>
  );

  const menu = useRef<Menu>(null);
  const actionItems = [
    { label: 'Vytvoriť províziu', icon: 'pi pi-plus', command: () => handleCreate() },
    { label: 'Pregenerovať nespracované provízie', icon: 'pi pi-refresh', command: () => handleRegenerate() },
    { label: 'Exportovať data', icon: 'pi pi-download', command: () => handleExportProvisions() },
  ];

  const renderHeader = () => {
    return (
      <div className="flex justify-content-between">
        <div>
          <Menu model={actionItems} popup ref={menu} id="actions_menu" />
          <Button icon="pi pi-cog" onClick={e => menu.current?.toggle(e)} aria-controls="actions_menu" aria-haspopup />
        </div>
        {/*<Button*/}
        {/*  style={{ backgroundColor: 'darkolivegreen' }}*/}
        {/*  type="button"*/}
        {/*  icon="pi pi-plus"*/}
        {/*  label="Vytvoriť províziu"*/}
        {/*  className="p-button-success"*/}
        {/*  onClick={handleCreate}*/}
        {/*/>*/}
        {/*<Button*/}
        {/*  style={{ backgroundColor: 'darkblue' }}*/}
        {/*  type="button"*/}
        {/*  icon="pi pi-refresh"*/}
        {/*  label="Pregenerovať nespracované provízie"*/}
        {/*  className="p-button-success"*/}
        {/*  onClick={handleRegenerate}*/}
        {/*/>*/}
        {/*<Button*/}
        {/*  style={{ backgroundColor: 'gray' }}*/}
        {/*  type="button"*/}
        {/*  icon="pi pi-download"*/}
        {/*  label="Exportovať data"*/}
        {/*  onClick={handleExportProvisions}*/}
        {/*/>*/}

        <div>
          <span>
            <label style={{ marginRight: '1rem' }}>Dátum nahratia:</label>
            <InputText
              type={'text'}
              value={rawAdditionalHeaderFilters?.from}
              onChange={e => setRawAdditionalHeaderFilters(prev => ({ ...prev, from: e.target.value }))}
              placeholder="Od (dd.MM.yyyy)"
              style={{ marginRight: '1rem' }}
            />
            <InputText
              type={'text'}
              value={rawAdditionalHeaderFilters?.to}
              onChange={e => setRawAdditionalHeaderFilters(prev => ({ ...prev, to: e.target.value }))}
              style={{ marginRight: '1rem' }}
              placeholder="Do (dd.MM.yyyy)"
            />
            <Button style={{ backgroundColor: '#e31818' }} icon="pi pi-search" type="button" onClick={onSearchFromTo}></Button>
          </span>
        </div>

        <div className="d-flex">
          <Dropdown
            id="insuranceCompany"
            name="insuranceCompany"
            value={selectedInsuranceCompany}
            options={insuranceCompanies}
            optionLabel="label"
            filter
            onChange={e => setSelectedInsuranceCompany(e.value)}
            placeholder="Importovať pre spoločnosť"
          />
          <ImportDataWidget
            importLabel="Importovať provízie"
            importApi={`/api/provisions/import/${selectedInsuranceCompany}`}
            importFileName="provízie-protokoly.txt"
            onFinishProcessing={loadData}
            disabled={!selectedInsuranceCompany}
          />
        </div>
      </div>
    );
  };

  const typProviziePodlaConcludedBy = rowData => {
    if (rowData?.type === 1) {
      return 'získateľská';
    } else if (rowData?.type === 2) {
      return 'rozdielová';
    }

    return '';
  };

  const expandTemplate = rowData => {
    return (
      <div className="p-ml-4">
        <h5 style={{ color: 'blue' }}>Spracovanie provízie - zobrazí sa na províznom liste</h5>
        <DataTable stripedRows value={rowData.agentProvisions} responsiveLayout="scroll" size="small">
          <Column field="agentName" header="Meno poradcu" />
          <Column field="positionName" header="Pozícia" />
          <Column field="unitValue" header="Hodnota J za pozíciu" />
          <Column field="type" header="Typ provízie" body={v => typProviziePodlaConcludedBy(v)} />
          <Column field="nrOfUnits" header="Počet jednotiek" />
          <Column field="provision" header="Provízia" />
          <Column field="period" header="Doba" />
        </DataTable>
      </div>
    );
  };

  const allowExpansion = rowData => {
    return rowData.agentProvisions.length > 0;
  };

  const isProcessedIcon = processed => {
    return (
      <span>
        {processed ? (
          <i className="pi pi-check" style={{ color: 'green', fontWeight: 'bold' }}></i>
        ) : (
          <i className="pi pi-times" style={{ color: 'red', fontWeight: 'bold' }}></i>
        )}
      </span>
    );
  };

  const header = renderHeader();

  const getMessagesIcons = data => {
    return (
      <span className="d-flex">
        {data.errors && <i className="pi pi-exclamation-triangle" title={data.errors} style={{ color: 'red', fontWeight: 'bold' }}></i>}

        {data.warnings && (
          <i className="pi pi-exclamation-triangle" title={data.warnings} style={{ color: 'orange', fontWeight: 'bold' }}></i>
        )}
      </span>
    );
  };

  const getProvisionExceedLimit = data => {
    if (data?.provisionsExceedLimit) {
      return (
        <span className="d-flex">
          <i
            className="pi pi-euro"
            title="Do siete sa dá vyššia suma ako sa reálne dostane"
            style={{ color: 'red', fontSize: '20px', fontWeight: 'bold' }}
          ></i>
        </span>
      );
    }

    return '';
  };

  const dialogFooter = (
    <React.Fragment>
      <Button
        style={{ backgroundColor: '#e31818' }}
        disabled={isProcessing}
        label="Stiahnuť protokoly"
        onClick={() => onDownloadProtocols(currentTaskId)}
      />
    </React.Fragment>
  );

  const formatLevel = level => {
    return <span style={{ color: level === 'ERROR' ? 'red' : level === 'WARN' ? 'orange' : 'blue' }}>{level}</span>;
  };

  const processedFilterMenu = options => {
    return (
      <Dropdown
        value={options.value}
        options={[
          { label: 'Všetko', value: null },
          { label: 'Spracované', value: true },
          { label: 'Nespracované', value: false },
        ]}
        onChange={e => options.filterApplyCallback(e.value)}
        placeholder="Vyber"
        className="p-column-filter"
        showClear
      />
    );
  };

  const typeFilterMenu = options => {
    return (
      <Dropdown
        value={options.value}
        options={[
          { label: 'Všetko', value: null },
          { label: 'Získateľská', value: 1 },
          { label: 'Následná', value: 2 },
        ]}
        onChange={e => options.filterApplyCallback(e.value)}
        placeholder="Vyber"
        className="p-column-filter"
        showClear
      />
    );
  };
  const riskFilterMenu = options => {
    return (
      <Dropdown
        value={options.value}
        options={[
          { label: 'Všetko', value: null },
          { label: 'Hlavné', value: 1 },
          { label: 'Vedľajšie', value: 2 },
          { label: 'X', value: 3 },
        ]}
        onChange={e => options.filterApplyCallback(e.value)}
        placeholder="Vyber"
        className="p-column-filter"
        showClear
      />
    );
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="card">
        <DataTable
          stripedRows
          value={provisions}
          size="small"
          lazy
          responsiveLayout="scroll"
          dataKey="id"
          emptyMessage="Žiadne dáta."
          header={header}
          paginator
          first={lazyParams.first}
          rows={20}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          rowExpansionTemplate={expandTemplate}
          expandedRows={expandedRows}
          onRowToggle={e => setExpandedRows(e.data)}
          // onRowExpand={onRowExpand}
          // onRowCollapse={onRowCollapse}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          filterDisplay="row"
          filters={filterValues}
          onFilter={e => setFilterValues(e.filters)}
          loading={loading}
        >
          <Column expander={allowExpansion} style={{ width: '5rem' }} />
          <Column field={'provisionsExceedLimit'} header="" body={v => getProvisionExceedLimit(v)} />
          <Column field={'messages'} header="" body={v => getMessagesIcons(v)} />
          <Column
            field={'processed'}
            filter={true}
            showFilterMenu={false}
            filterElement={processedFilterMenu}
            header="Spr."
            sortable={true}
            body={v => isProcessedIcon(v.processed)}
          />
          <Column field={'id'} filter={true} showFilterMenu={false} header="ID" sortable={true} />
          <Column
            field={'type'}
            filter={true}
            showFilterMenu={false}
            filterElement={typeFilterMenu}
            header="Typ provízie"
            sortable={true}
          />
          <Column field={'risk'} filter={true} showFilterMenu={false} filterElement={riskFilterMenu} header="Riziko" sortable={true} />
          <Column field={'proposalNr'} filter={true} showFilterMenu={false} header="Č. návrhu" sortable={true} />
          <Column field={'clientName'} filter={true} showFilterMenu={false} header="Klient" sortable={true} />
          <Column field={'zaklad'} header="Základ" sortable={true} />
          <Column field={'percento'} header="Percento" sortable={true} />
          <Column field={'provizia'} header="Provízia" sortable={true} />
          <Column field={'doba'} header="Doba" sortable={true} />
          <Column
            field={'importedProvisionFile.insuranceCompany.name'}
            filter={true}
            sortable={false}
            showFilterMenu={false}
            filterField={'importedInsuranceCompany'}
            header="Importovaná pre"
          />
          <Column
            field={'importedProvisionFile.insuranceCompany.name'}
            filter={true}
            sortable={false}
            showFilterMenu={false}
            filterField={'insuranceCompany'}
            header="Spoločnosť"
          />
          <Column field={'from'} header="Obdobie od" sortable={true} body={v => formatFromLocalDate(v.from)} />
          <Column field={'to'} header="Obdobie do" sortable={true} body={v => formatFromLocalDate(v.to)} />
          <Column field={'createdDate'} header="Importované" sortable={true} body={v => formatDateFromInstant(v.createdDate)} />
          <Column body={actionBodyTemplate} header="Akcia" />
        </DataTable>
      </div>
      <Dialog
        visible={deleteContactDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header="Potvrdenie"
        modal
        footer={deleteProductDialogFooter}
        onHide={hideDeleteContactDialog}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          {entity && (
            <span>
              Naozaj chcete vymazať proviziu pre: <b>{entity.clientName + ' ' + '(' + entity.type + ' - ' + entity.risk + ')'}</b>?
            </span>
          )}
        </div>
      </Dialog>
      {showDialog && (
        <Dialog
          footer={dialogFooter}
          visible={showDialog}
          style={{ width: '90%' }}
          header="Protokoly importu"
          onHide={() => setShowDialog(false)}
        >
          <div style={{ width: '100%' }}>
            <DataTable
              stripedRows
              emptyMessage="Žiadne protokoly"
              size="small"
              lazy
              value={statusMessages}
              responsiveLayout="scroll"
              className="p-mt-4"
            >
              <Column field="level" header="Level" body={v => formatLevel(v.level)} />
              <Column field="message" header="Správa" />
              <Column field="timestamp" header="Čas" body={v => formatDateFromInstant(v.timestamp)} />
            </DataTable>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default ProvisionTable;
